/**
 * Taken from https://github.com/mantaskaveckas/react-siema and rewritten using react-hooks
 */
import React, {
  FC, Children, useEffect, useRef, useState
} from 'react';
import styled, { css } from 'styled-components';

import { Icon } from '@codex-brightworld/icons';

import CarouselNavigation from './CarouselNavigation/CarouselNavigation';

const ComponentWrapper = styled.div`
  position: relative;
`;
const CarouselWrapper = styled.div<{
  disabled: boolean
}>`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  height: 100%;
  ${props => props.disabled && css`
    cursor: default;
    pointer-events: none;
    touch-action: none;
  `};
`;
const CarouselDisabledOverlay = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background :white;
    opacity: 0.85;
    z-index: 1;
`;

const SliderWrapper = styled.div`
  overflow: hidden;
  display: block;
  width: 100%;
  height: 100%;
`;

const SliderFrame = styled.div<{
  draggingInProgress: boolean
}>`
  height: 100%;
  * {
    // This corrects flickering during CSS transition/transformation in Safari
    -webkit-transform-style: preserve-3d;
    -webkit-backface-visibility: hidden;
  }

  // when dragging is in progress we have to disable clicking and touching
  // for case when there are some <a> elements in the content which would lead to redirection
  // to URL in href attribute
  ${props => props.draggingInProgress && css`
    a {
      cursor: default;
      pointer-events: none;
      touch-action: none;
    }
  `}
`;

const SlideWrapper = styled.div`
  display: inline-block;
  vertical-align: middle;
  height: 100%;
`;

// colors only for test on other app
const ArrowButton = styled(Icon)<{
  buttonDisabled: boolean
}>`
  min-width: 28px;
  cursor: ${props => (props.buttonDisabled ? 'default' : 'pointer')};
  svg path:last-child {
    fill: ${props => (props.buttonDisabled
    ? 'red'
    : 'green')};
  }
`;


type Props = {
  /** Slides */
  children: Array<JSX.Element>,
  /** turn on/off slide navigation below carousel */
  showNavigation?: boolean,
  /** turn on/off arrow buttons */
  showArrowButtons?: boolean,
  /** duration of animation of switching slides in miliseconds */
  duration?: number,
  /** easing type of transition effect of switching slides animation */
  easing?: 'ease' | 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out',
  /** turn on/off looping for getting from the last slide to the first slide and vice versa */
  loop?: boolean,
  /** this prop allows setting currently selected slide in the Carousel from outside of the component */
  selectSlideIndex?: number,
  /** this function is called when selected slide has changed */
  onSelectedSlideChanged?: (selectedSlideIndex?: number) => void,
  /** Minimal threshold in pixels which has to be performed by sliding (by mouse or fingers) for switching to another slide */
  threshold?: number,
  /** Disables whole interactive functionality of the Carousel  */
  disabled?: boolean,
  /** Value of data-qa HTML attribute for testing purposes  */
  qa?: string,
  /** User specified classes for custom styling the whole component  */
  className?: string,
  /** User specified classes for custom styling Carousel Slider wrapper  */
  carouselSliderWrapperClassname?: string,
  /** User specified classes for custom styling Carousel Navigation  */
  carouselNavigationClassname?: string
};

type DragState = {
  dragStartPosition: number | null,
  dragEndPosition: number | null,
  dragPhase: 'noMouseOrTouchInteraction' | 'afterMouseOrTouchDown' | 'afterClickOrTouchWithoutDrag' |
    'afterSingleDrag' | 'afterFinishingDragging'
}


export const Carousel: FC<Props> = ({
  children, showNavigation = true,
  showArrowButtons = true, duration = 200, easing = 'ease-out', loop = false,
  selectSlideIndex = 0, onSelectedSlideChanged = () => {}, threshold = 20, disabled = false,
  qa, className, carouselSliderWrapperClassname = undefined,
  carouselNavigationClassname = undefined
}) => {
  const sliderWrapperElement = useRef<HTMLDivElement>(null);
  const sliderFrameElement = useRef<HTMLDivElement>(null);

  const [componentInitialized, setComponentInitialized] = useState<boolean>(false);

  // it contains index of the current slide visible in the viewport

  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
  const [dragState, setDragState] = useState<DragState>({
    dragStartPosition: null,
    dragEndPosition: null,
    dragPhase: 'noMouseOrTouchInteraction'
  });

  // initializes sizes of SliderWrapper and SliderFrame components
  const initializeSizesOfComponents = () => {
    if (sliderWrapperElement.current === null || sliderFrameElement.current === null) {
      return;
    }

    const sliderWrapperElementWidth = sliderWrapperElement.current.getBoundingClientRect().width;
    const sliderFrameElementChildrenLength = sliderFrameElement.current.children.length;

    // calculates and set size slides frame
    sliderFrameElement.current.style.width = `${(sliderWrapperElementWidth) * sliderFrameElementChildrenLength}px`;
    sliderFrameElement.current.style.transition = `all ${duration}ms ${easing}`;

    // calculates and set size of each slide in order to be the the same for all slides
    for (let i = 0; i < sliderFrameElementChildrenLength; i++) {
      if (sliderFrameElement.current.children[i] instanceof HTMLElement) {
        const childHTMLElement = sliderFrameElement.current.children[i] as HTMLElement;
        childHTMLElement.style.width = `${100 / sliderFrameElementChildrenLength}%`;
      }

    }
  };

  // scrolls to slide
  const scrollToSlide = (slideNumber: number | null) => {
    if (sliderWrapperElement.current !== null && sliderFrameElement.current !== null && slideNumber !== null) {
      const sliderWrapperElementWidth = sliderWrapperElement.current.getBoundingClientRect().width;
      sliderFrameElement.current.style.transform =
        `translate3d(-${slideNumber * (sliderWrapperElementWidth)}px, 0, 0)`;
    }
  };

  // detects if it is necessary to scroll to another slide
  const updateCurrentSlide = () => {
    if (sliderFrameElement.current === null || dragState.dragEndPosition === null || dragState.dragStartPosition === null ||
      currentSlideIndex === null) {
      return;
    }

    const sliderFrameElementChildrenLength = sliderFrameElement.current.children.length;
    const movement = dragState.dragEndPosition - dragState.dragStartPosition;

    if (movement > 0 && Math.abs(movement) > threshold) {
      // scrolling to previous slide if it is possible
      if (currentSlideIndex === 0) {
        if (loop) {
          setCurrentSlideIndex(sliderFrameElementChildrenLength - 1);
        } else {
          // scrolling to previous slide not possible so scroll back to the current slide
          scrollToSlide(currentSlideIndex);
        }
      } else {
        setCurrentSlideIndex(currentSlideIndex - 1);
      }
    } else if (movement < 0 && Math.abs(movement) > threshold) {
      // scrolling to next slide if it is possible
      if (currentSlideIndex === sliderFrameElementChildrenLength - 1) {
        if (loop) {
          setCurrentSlideIndex(0);
        } else {
          // scrolling to next slide not possible so scroll back to the current slide
          scrollToSlide(currentSlideIndex);
        }
      } else {
        setCurrentSlideIndex(currentSlideIndex + 1);
      }
    } else {
      // no movement or movement was shorter than threshold, so scroll back to currentSlide
      scrollToSlide(currentSlideIndex);
    }
  };

  // init of the component
  useEffect(() => {
    // init of component
    if (!componentInitialized) {
      initializeSizesOfComponents();
      setCurrentSlideIndex(selectSlideIndex);
      setComponentInitialized(true);
    }

  }, [componentInitialized]);

  // changes currentSlide when selectedSlideIndex changes
  useEffect(() => {
    if (componentInitialized) {
      setCurrentSlideIndex(selectSlideIndex);
    }

  }, [componentInitialized, selectSlideIndex]);

  // when currentSlideIndex changes we need to scroll to the new slide with the index
  useEffect(() => {
    scrollToSlide(currentSlideIndex);
    onSelectedSlideChanged(currentSlideIndex);
  }, [currentSlideIndex]);

  // create resize event listener
  useEffect(() => {
    const onResize = () => {
      initializeSizesOfComponents();
      scrollToSlide(currentSlideIndex);
      onSelectedSlideChanged(currentSlideIndex);
    };

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  });


  // when state of dragging change
  useEffect(() => {
    if (dragState.dragPhase === 'noMouseOrTouchInteraction') {
      // user doesn't do any mouse or touch interaction with Carousel, so nothing to do
      return;
    }

    if (dragState.dragPhase === 'afterSingleDrag' && sliderWrapperElement.current !== null &&
      sliderFrameElement.current !== null && currentSlideIndex !== null &&
    dragState.dragStartPosition !== null && dragState.dragEndPosition !== null) {
      // user has performed single drag by moving a mouse or a finger
      // so we have to perform animation
      const sliderWrapperElementWidth = sliderWrapperElement.current.getBoundingClientRect().width;
      sliderFrameElement.current.style.cursor = 'grabbing';
      sliderFrameElement.current.style.transition = `all 0ms ${easing}`;
      sliderFrameElement.current.style.transform = `translate3d(${((currentSlideIndex *
        (sliderWrapperElementWidth)) +
        (dragState.dragStartPosition - dragState.dragEndPosition)) * -1}px, 0, 0)`;
    }

    if (dragState.dragPhase === 'afterFinishingDragging' ||
      dragState.dragPhase === 'afterClickOrTouchWithoutDrag') {

      if (dragState.dragPhase === 'afterFinishingDragging') {
        // user finished dragging so we need to perform animation and change slide if necessary
        if (sliderFrameElement.current !== null) {
          sliderFrameElement.current.style.cursor = 'grab';
          sliderFrameElement.current.style.transition = `all ${duration}ms ${easing}`;
        }
        updateCurrentSlide();
      }

      // resetting dragState after end of dragging
      // or after user's click or touch without dragging
      setDragState({
        dragStartPosition: null,
        dragEndPosition: null,
        dragPhase: 'noMouseOrTouchInteraction'
      });
    }
  }, [dragState]);


  return (
    <ComponentWrapper className={className} data-qa={qa}>
      {disabled ? <CarouselDisabledOverlay /> : null}
      <CarouselWrapper disabled={disabled}>
        { showArrowButtons && children.length > 1 && (
          <ArrowButton
            buttonDisabled={!loop && currentSlideIndex === 0}
            icon="arrowright"
            rotate={180}
            size="large"
            onClick={(e) => {
              e.preventDefault();

              if (currentSlideIndex !== null && currentSlideIndex !== 0) {
                setCurrentSlideIndex(currentSlideIndex - 1);
              } else if (loop && sliderFrameElement.current !== null) {
                setCurrentSlideIndex(sliderFrameElement.current.children.length - 1);
              }
            }}
          />
        )}
        <SliderWrapper
          ref={sliderWrapperElement}
          className={carouselSliderWrapperClassname}
          onTouchStart={(e) => {
            const dragStartPosition = e.touches[0].pageX;
            setDragState(prevState => ({
              ...prevState,
              dragStartPosition,
              dragPhase: 'afterMouseOrTouchDown'
            }));
          }}
          onTouchEnd={() => {
            if (dragState.dragEndPosition === null) {
              setDragState(prevState => ({
                ...prevState,
                dragPhase: 'afterClickOrTouchWithoutDrag'
              }));
            } else {
              setDragState(prevState => ({
                ...prevState,
                dragPhase: 'afterFinishingDragging'
              }));
            }
          }}
          onTouchMove={(e) => {
            if (dragState.dragPhase === 'afterMouseOrTouchDown' ||
              dragState.dragPhase === 'afterSingleDrag') {
              const dragEndPosition = e.touches[0].pageX;

              setDragState(prevState => ({
                ...prevState,
                dragEndPosition,
                dragPhase: 'afterSingleDrag'
              }));
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();

            const dragStartPosition = e.pageX;
            setDragState(prevState => ({
              ...prevState,
              dragStartPosition,
              dragPhase: 'afterMouseOrTouchDown'
            }));
          }}
          onMouseUp={(e) => {
            e.preventDefault();

            if (dragState.dragEndPosition === null) {
              setDragState(prevState => ({
                ...prevState,
                dragPhase: 'afterClickOrTouchWithoutDrag'
              }));
            } else {
              setDragState(prevState => ({
                ...prevState,
                dragPhase: 'afterFinishingDragging'
              }));
            }
          }}
          onMouseLeave={(e) => {
            e.preventDefault();

            if (dragState.dragPhase === 'afterSingleDrag') {
              const dragEndPosition = e.pageX;

              setDragState(prevState => ({
                ...prevState,
                dragEndPosition,
                dragPhase: 'afterFinishingDragging'
              }));
            }
          }}
          onMouseMove={(e) => {
            e.preventDefault();
            if (dragState.dragPhase === 'afterMouseOrTouchDown' ||
              dragState.dragPhase === 'afterSingleDrag') {
              const dragEndPosition = e.pageX;

              setDragState(prevState => ({
                ...prevState,
                dragEndPosition,
                dragPhase: 'afterSingleDrag'
              }));
            }
          }}
        >
          <SliderFrame
            ref={sliderFrameElement}
            draggingInProgress={dragState.dragPhase === 'afterSingleDrag'}
          >
            {Children.map(children, child => (
              <SlideWrapper>
                {child}
              </SlideWrapper>
            ))}
          </SliderFrame>
        </SliderWrapper>
        { showArrowButtons && children.length > 1 && (
          <ArrowButton
            buttonDisabled={!loop && currentSlideIndex === children.length - 1}
            icon="arrowright"
            rotate={0}
            size="large"
            onClick={(e) => {
              e.preventDefault();
              if (currentSlideIndex !== null && currentSlideIndex !== children.length - 1) {
                setCurrentSlideIndex(currentSlideIndex + 1);
              } else if (loop) {
                setCurrentSlideIndex(0);
              }
            }}
          />
        )}
      </CarouselWrapper>
      {showNavigation && children.length > 1 && (
        <CarouselNavigation
          className={carouselNavigationClassname}
          currentNavigationItemIndex={currentSlideIndex !== null ? currentSlideIndex : undefined}
          onNavigationItemClick={(navigationItemIndex) => {
            setCurrentSlideIndex(
              Math.min(
                Math.max(navigationItemIndex, 0),
                sliderFrameElement.current !== null
                  ? sliderFrameElement.current.children.length - 1
                  : 0
              )
            );
          }}
          numberOfNavigationItems={children.length}
        />
      )}
    </ComponentWrapper>
  );
};
